import React, { useEffect, useState } from 'react'
import { DocumentDTO, OrderDto } from '../../services/dtos'
import { MedicalOrder, Report } from '../../services/api'
import {
  ListItemText,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Paper,
  Stack,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { doFetchReports } from '../../state/reducers/reportReducer'
import DocumentListViewer from '../dialogs/DocumentListViewer'
import {
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  NoteAdd as NoteAddIcon,
  Assignment as AssignmentIcon,
  LockOpen,
} from '@mui/icons-material'
import { DictationStatusBadge } from './DictationStatusBadge'
import { DmpStatusIndicator, DmpSubmissionStatus } from './DmpStatus'
import { ReleaseHeldDmpTask } from '../../containers/app/procedure/ReleaseHeldDmpTask'
import { doReleaseHeldBackgroundTasks } from '@state/thunks/backgroundTaskThunk'

type Props = {
  order: OrderDto
  onSelectReport: (url: string) => void
}

const ReportSelection = ({ order, onSelectReport }: Props) => {
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()
  const { reports } = useAppSelector((state) => state.reports)

  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false)
  const [selectedTaskUid, setSelectedTaskUid] = useState<string | null>(null)

  if (!order.medicalOrders || order.medicalOrders.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          color: 'text.secondary',
        }}
      >
        <AssignmentIcon sx={{ fontSize: 40, opacity: 0.4 }} />
        <Typography variant="body2">No medical orders found.</Typography>
      </Box>
    )
  }

  const handleConfirmRelease = async (delayInMs: number) => {
    if (!selectedTaskUid) return
    await dispatch(
      doReleaseHeldBackgroundTasks({
        uid: selectedTaskUid,
        body: { delayInMs },
      }),
    ).unwrap()
  }

  const handleOpenRelease = (uid: string) => {
    setSelectedTaskUid(uid)
    setReleaseDialogOpen(true)
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(
      doFetchReports({
        medicalOrderId: order.medicalOrders.map((mo) => mo.id),
      }),
    )
      .unwrap()
      .finally(() => {
        setIsLoading(false)
      })
  }, [order.medicalOrders, dispatch])

  return (
    <Box sx={{ width: '100%', p: 1.5 }}>
      <Stack spacing={1.5}>
        {order.medicalOrders.map((mo: MedicalOrder) => {
          const examLabel = mo.exam?.label || 'N/A'
          const examCode = mo.exam?.code || 'N/A'

          const moReports = reports.filter(
            (report) => report.medicalOrderId === mo.id,
          )

          const reportActions: { url: string; filename: string; id: number }[] =
            []

          if (moReports.length > 0) {
            const url = `${process.env.REACT_APP_API_URL}/static/${moReports[0].ged_file.filePath}/${moReports[0].ged_file.filename}`
            reportActions.push({
              url,
              filename: moReports[0].ged_file.filename,
              id: moReports[0].id,
            })
          }

          const hasReport = reportActions.length > 0
          const heldTaskUid = mo.backgroundTasks?.[0]?.uid
          const dmpsubmission = mo.DmpSubmission?.[0]

          return (
            <Paper
              key={mo.id}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  borderColor: 'primary.light',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <ListItemText
                    primary={examLabel}
                    secondary={examCode}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      component: 'span',
                    }}
                    sx={{ m: 0 }}
                  />
                  {dmpsubmission && (
                    <DmpStatusIndicator
                      status={dmpsubmission?.status as DmpSubmissionStatus}
                      errorMessage={dmpsubmission?.error}
                      submittedAt={dmpsubmission?.createdAt}
                    />
                  )}
                  {heldTaskUid && (
                    <Tooltip title="Envoyer le DMP en attente" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRelease(heldTaskUid)}
                      >
                        <LockOpen />
                      </IconButton>
                    </Tooltip>
                  )}
                  {mo.DictationReportStatus && (
                    <DictationStatusBadge status={mo.DictationReportStatus} />
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={18} />
                  ) : hasReport ? (
                    <>
                      <Tooltip title={reportActions[0].filename} arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setSelectedReport(moReports[0])}
                        >
                          <PdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Modifier CR" arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() =>
                            onSelectReport(
                              `${process.env.REACT_APP_REPORTING_URL}/iframe/medical-orders/${mo.id}/report`,
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        <NoteAddIcon sx={{ fontSize: '16px !important' }} />
                      }
                      onClick={() =>
                        onSelectReport(
                          `${process.env.REACT_APP_REPORTING_URL}/iframe/medical-orders/${mo.id}/report`,
                        )
                      }
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        fontSize: '0.8rem',
                        px: 1.5,
                      }}
                    >
                      Créer CR
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          )
        })}
      </Stack>

      <DocumentListViewer
        isOpen={!!selectedReport}
        onSelectedDocumentChange={() => setSelectedReport(null)}
        onClose={() => setSelectedReport(null)}
        selectedDocument={
          selectedReport
            ? ({
                id: selectedReport?.id || -1,
                url: `static/${selectedReport?.ged_file.filePath}/${selectedReport?.ged_file?.filename}`,
                gedFile: selectedReport?.ged_file,
              } as DocumentDTO)
            : null
        }
        documents={
          selectedReport
            ? [
                {
                  id: selectedReport?.id || -1,
                  url: `static/=${selectedReport?.ged_file.filePath}/${selectedReport?.ged_file?.filename}`,
                  gedFile: selectedReport?.ged_file,
                } as DocumentDTO,
              ]
            : []
        }
      />
      <ReleaseHeldDmpTask
        open={releaseDialogOpen}
        onClose={() => setReleaseDialogOpen(false)}
        onConfirm={handleConfirmRelease}
        taskUid={selectedTaskUid ?? ''}
      />
    </Box>
  )
}

export default ReportSelection
